import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Phaser from 'phaser';
import {
  clearAllPlaytestArenaLocalCaches,
  usePlaytestArenaFiles,
} from '../../hooks/usePlaytestArenaFiles';
import {
  buildPlaytestAssetUrlMap,
  remapPlaytestAssetUrl,
} from '../../lib/playtestAssetUrlMap';
import { compileApprovedGameFiles as compileApprovedGameFilesImpl } from '../../lib/playtestArenaCompile';
import {
  PLAYTEST_DEFAULT_HEIGHT,
  PLAYTEST_DEFAULT_WIDTH,
  PLAYTEST_MOUNT_MAX_HEIGHT,
  readArcadeGravityY,
  readGameConfigFromRegistry,
  resolvePlaytestScale,
} from '../../lib/playtestArenaScale';

/** @deprecated Prefer importing from lib/playtestArenaCompile — kept for callers. */
export function compileApprovedGameFiles(approvedFiles) {
  return compileApprovedGameFilesImpl(approvedFiles, Phaser);
}

/** Placeholder scene shown until AI-generated game code is loaded. */
class PlaceholderScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PlaceholderScene' });
  }

  create() {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#1a1a2e');

    this.add
      .text(width / 2, height / 2, 'AI Game Engine Ready - Waiting for Code', {
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '20px',
        color: '#e8e8f0',
        align: 'center',
        wordWrap: { width: Math.max(width - 48, 200) },
      })
      .setOrigin(0.5);
  }
}

/**
 * Rewrite relative Phaser load URLs (assets/foo.png) to gallery Storage
 * download URLs so Playtest Arena does not hit the admin SPA origin
 * (which rewrites unknown paths to index.html → "Failed to process file").
 *
 * Prefer URL rewrite over blob URLs (no revoke lifecycle unless CORS forces it).
 *
 * Timing note: SceneManager calls sys.start() (plugins hear `start`) THEN
 * bootScene() → loader.reset() → scene.preload(). Any files queued on
 * `start` are wiped by reset(). Hooks must survive reset and preferably
 * re-prime the queue inside a wrapped reset().
 */
class PlaytestAssetUrlRewritePlugin extends Phaser.Plugins.ScenePlugin {
  boot() {
    const eventEmitter = this.systems.events;
    eventEmitter.on('start', this.onSceneStart, this);
    eventEmitter.once('destroy', this.onSceneDestroy, this);
  }

  onSceneDestroy() {
    this.systems.events.off('start', this.onSceneStart, this);
  }

  /**
   * @param {Record<string, string>} map
   * @param {Phaser.Loader.LoaderPlugin} loader
   */
  installLoaderHooks(map, loader) {
    if (!loader || loader.__playtestUrlHooksInstalled) return;
    loader.__playtestUrlHooksInstalled = true;

    const remapFileUrl = (key, url) => {
      const next = remapPlaytestAssetUrl(key, url, map);
      if (next && next !== url) {
        console.info(`[PlaytestAssetUrl] remapped ${key} → Storage download URL`);
      }
      return next;
    };

    const wrap = (methodName) => {
      if (typeof loader[methodName] !== 'function') return;
      if (loader[`__playtestWrapped_${methodName}`]) return;
      loader[`__playtestWrapped_${methodName}`] = true;
      const orig = loader[methodName].bind(loader);
      loader[methodName] = (...args) => {
        if (args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
          const cfg = { ...args[0] };
          cfg.url = remapFileUrl(cfg.key, cfg.url);
          return orig(cfg, ...args.slice(1));
        }
        const key = args[0];
        const url = args[1];
        return orig(key, remapFileUrl(key, url), ...args.slice(2));
      };
    };

    wrap('image');
    wrap('spritesheet');

    // Mutate File.url after path prefixing (catches every image-like add).
    if (typeof loader.addFile === 'function' && !loader.__playtestAddFileWrapped) {
      loader.__playtestAddFileWrapped = true;
      const origAddFile = loader.addFile.bind(loader);
      loader.addFile = (file) => {
        const files = Array.isArray(file) ? file : [file];
        files.forEach((item) => {
          if (!item || typeof item !== 'object') return;
          if (item.type !== 'image' && item.type !== 'spritesheet') return;
          const prev = item.url;
          const next = remapFileUrl(item.key, item.url);
          if (next && next !== prev) {
            item.url = next;
          }
        });
        return origAddFile(file);
      };
    }

    if (typeof loader.reset === 'function' && !loader.__playtestResetWrapped) {
      loader.__playtestResetWrapped = true;
      const origReset = loader.reset.bind(loader);
      loader.reset = (...resetArgs) => {
        const result = origReset(...resetArgs);
        // reset() clears the queue after Scene `start`. Re-prime Storage
        // URLs here so they win before scene.preload() runs.
        this.prequeueSemanticTextures(map, loader);
        return result;
      };
    }
  }

  /**
   * Queue one load.image per semantic basename (no path / extension).
   * Preload duplicates are skipped by Phaser keyExists.
   * @param {Record<string, string>} map
   * @param {Phaser.Loader.LoaderPlugin} loader
   */
  prequeueSemanticTextures(map, loader) {
    if (!loader || typeof loader.image !== 'function') return;
    const queued = new Set();
    Object.keys(map || {}).forEach((lookupKey) => {
      if (!lookupKey || lookupKey.includes('/') || lookupKey.includes('.')) {
        return;
      }
      const url = map[lookupKey];
      if (!url || !/^https?:\/\//i.test(url)) return;
      if (queued.has(lookupKey)) return;
      queued.add(lookupKey);
      try {
        loader.image(lookupKey, url);
      } catch (err) {
        console.warn(
          `[PlaytestAssetUrl] prequeue failed for ${lookupKey}:`,
          err,
        );
      }
    });
    if (queued.size) {
      console.info(
        `[PlaytestAssetUrl] pre-queued ${queued.size} gallery texture(s) after loader.reset`,
      );
    }
  }

  onSceneStart() {
    const map = this.game.registry.get('playtestAssetUrlMap') || {};
    const loader = this.scene.load;
    if (!loader) return;
    this.installLoaderHooks(map, loader);
  }
}

/**
 * When AI scenes call load.image/spritesheet('player'|…) against missing
 * repo assets, Phaser throws "Texture/Frame not found". This plugin injects
 * multi-frame placeholder sheets so generateFrameNumbers still works.
 *
 * Important: SPA hosting often returns index.html with HTTP 200 for missing
 * `/assets/*.png`. Phaser XHR then "succeeds" and fails in onProcess — that
 * path does NOT emit `loaderror`. We also hook fileProcessComplete for
 * FILE_ERRORED so placeholders still appear instead of green __MISSING.
 *
 * Do not pre-seed texture keys: TextureManager.exists → loader.keyExists
 * silently skips the real gallery/Storage download.
 */
class PlaytestAssetFallbackPlugin extends Phaser.Plugins.ScenePlugin {
  boot() {
    const eventEmitter = this.systems.events;
    eventEmitter.on('start', this.onSceneStart, this);
    eventEmitter.once('destroy', this.onSceneDestroy, this);
  }

  onSceneDestroy() {
    this.systems.events.off('start', this.onSceneStart, this);
  }

  /**
   * Create a solid sheet with numbered frames (0..frameCount-1).
   * @param {string} key
   * @param {number} [frameWidth]
   * @param {number} [frameHeight]
   * @param {number} [color]
   * @param {number} [frameCount]
   */
  ensureTexture(
    key,
    frameWidth = 32,
    frameHeight = 32,
    color = 0x7c4dff,
    frameCount = 1,
  ) {
    if (!key) return;
    const frames = Math.max(1, Math.floor(frameCount) || 1);
    const textures = this.scene.textures;

    if (textures.exists(key)) {
      const existing = textures.get(key);
      // Rebuild if a single-frame stub can't satisfy spritesheet anims.
      if (frames <= 1 || existing.has(0) || existing.has('0')) return;
      textures.remove(key);
    }

    const totalW = frameWidth * frames;
    const g = this.scene.make.graphics({ x: 0, y: 0, add: false });
    for (let i = 0; i < frames; i += 1) {
      const shade = color;
      g.fillStyle(shade, 1);
      g.fillRect(i * frameWidth, 0, frameWidth, frameHeight);
      g.lineStyle(1, 0xffffff, 0.45);
      g.strokeRect(i * frameWidth + 1, 1, frameWidth - 2, frameHeight - 2);
      // Tiny index mark so frames are distinct for debugging.
      g.fillStyle(0xffffff, 0.35);
      g.fillRect(i * frameWidth + 4, 4, 4, 4);
    }
    g.generateTexture(key, totalW, frameHeight);
    g.destroy();

    if (frames > 1) {
      const tex = textures.get(key);
      for (let i = 0; i < frames; i += 1) {
        if (!tex.has(i) && !tex.has(String(i))) {
          tex.add(i, 0, i * frameWidth, 0, frameWidth, frameHeight);
        }
      }
    }
  }

  /**
   * @param {string} key
   */
  injectPlaceholderForKey(key) {
    if (!key || key.startsWith('__')) return;
    console.warn(
      `[PhaserGameMount] Missing asset "${key}" — injecting placeholder texture`,
    );
    const isGround = /ground|platform|floor/i.test(key);
    const isPlayer = /player|hero|sprite/i.test(key);
    this.ensureTexture(
      key,
      isGround ? 64 : 32,
      isGround ? 16 : isPlayer ? 48 : 32,
      isGround ? 0x2d3436 : isPlayer ? 0x00cec9 : 0x7c4dff,
      isPlayer ? 8 : 1,
    );
  }

  onSceneStart() {
    const loader = this.scene.load;
    if (!loader || typeof loader.on !== 'function') return;

    if (!loader.__playtestFallbackHooksInstalled) {
      loader.__playtestFallbackHooksInstalled = true;

      loader.on('loaderror', (file) => {
        const key = file && typeof file.key === 'string' ? file.key : '';
        this.injectPlaceholderForKey(key);
      });

      if (typeof loader.fileProcessComplete === 'function') {
        const origComplete = loader.fileProcessComplete.bind(loader);
        loader.fileProcessComplete = (file) => {
          // Phaser FILE_ERRORED === 16 (onProcess decode failure after HTTP 200).
          if (
            file &&
            file.state === 16 &&
            typeof file.key === 'string' &&
            file.key
          ) {
            this.injectPlaceholderForKey(file.key);
          }
          return origComplete(file);
        };
      }
    }
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function asTrimmedString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Mounts a Phaser 3 instance and injects approved AI game file strings.
 * When the project has a GitHub repo, prefers a cache-busted pull from main
 * over stale sandbox / localStorage payloads.
 *
 * @param {{
 *   height?: number | string,
 *   className?: string,
 *   companyId?: string | null,
 *   selectedProject?: object | null,
 *   approvedFiles?: Array<object> | null,
 *   galleryAssets?: Array<object> | null,
 * }} props
 */
export default function PhaserGameMount({
  height = 400,
  className,
  companyId = null,
  selectedProject = null,
  approvedFiles = null,
  galleryAssets = null,
}) {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const playtestAssetUrlMap = useMemo(
    () => buildPlaytestAssetUrlMap(galleryAssets),
    [galleryAssets],
  );
  const playtestAssetUrlMapKey = useMemo(
    () =>
      Object.keys(playtestAssetUrlMap)
        .sort()
        .map((k) => `${k}=>${playtestAssetUrlMap[k]?.slice?.(0, 48) || ''}`)
        .join('|'),
    [playtestAssetUrlMap],
  );
  const remappedTextureCount = useMemo(() => {
    const urls = new Set(Object.values(playtestAssetUrlMap));
    return urls.size;
  }, [playtestAssetUrlMap]);

  const projectId =
    (selectedProject &&
      (selectedProject.id ||
        selectedProject.projectId ||
        selectedProject.gameId)) ||
    '';
  const hasGithubRepo = Boolean(
    (selectedProject &&
      (selectedProject.githubRepoUrl ||
        (selectedProject.githubOwner && selectedProject.githubRepoName))) ||
      false,
  );

  const {
    files: githubFiles,
    loading: githubLoading,
    error: githubError,
    meta: githubMeta,
  } = usePlaytestArenaFiles({
    companyId,
    gameId: projectId,
    enabled: Boolean(companyId && projectId),
    hasGithubRepo,
    refreshToken,
  });

  const effectiveFiles = useMemo(() => {
    // Live GitHub tip only when it actually returned files.
    if (hasGithubRepo && githubFiles.length > 0) return githubFiles;
    // While pulling, keep the canvas empty (loading overlay).
    if (hasGithubRepo && githubLoading) return [];
    // Linked repo but GitHub 404/PAT failure: do NOT fall back to
    // company-wide sandbox history (that re-injects unrelated platformer
    // Player.js and crashes Pong). Prefer an empty/placeholder arena.
    if (hasGithubRepo && githubError) return [];
    return Array.isArray(approvedFiles) ? approvedFiles : [];
  }, [
    hasGithubRepo,
    githubFiles,
    githubLoading,
    githubError,
    approvedFiles,
  ]);

  const filesKey = useMemo(() => {
    const rows = Array.isArray(effectiveFiles) ? effectiveFiles : [];
    const body = rows
      .map((row) => {
        if (!row || typeof row !== 'object') return '';
        const path =
          asTrimmedString(row.path) ||
          asTrimmedString(row.target_file_path) ||
          asTrimmedString(row.file_path) ||
          asTrimmedString(row.id);
        const content =
          asTrimmedString(row.content) ||
          asTrimmedString(row.patch_content) ||
          asTrimmedString(row.source);
        return `${path}::${content.length}::${content.slice(0, 64)}`;
      })
      .join('|');
    const tip = githubMeta?.commitSha || '';
    const bust = githubMeta?.cacheBust || refreshToken || 0;
    return `${tip}::${bust}::${playtestAssetUrlMapKey}::${body}`;
  }, [effectiveFiles, githubMeta, refreshToken, playtestAssetUrlMapKey]);

  const projectKey =
    (selectedProject &&
      (selectedProject.id || selectedProject.projectId || selectedProject.title)) ||
    '';

  const platformerAssetWarning = useMemo(() => {
    const rows = Array.isArray(effectiveFiles) ? effectiveFiles : [];
    const hit = rows.some((row) => {
      const content =
        asTrimmedString(row?.content) ||
        asTrimmedString(row?.patch_content) ||
        asTrimmedString(row?.source);
      return (
        /load\.(image|spritesheet)\(\s*['"]player['"]/i.test(content) ||
        /load\.(image|spritesheet)\(\s*['"]ground['"]/i.test(content)
      );
    });
    return hit
      ? 'GitHub main still references platformer assets (player/ground). Placeholders are injected so playtest can boot — merge Pong scenes to main to replace this scaffold.'
      : '';
  }, [effectiveFiles]);

  const compiledBundle = useMemo(() => {
    try {
      return compileApprovedGameFilesImpl(
        Array.isArray(effectiveFiles) ? effectiveFiles : [],
        Phaser,
      );
    } catch (err) {
      console.error('[PhaserGameMount] compile pipeline crashed:', err);
      return { scenes: [], errors: [String(err)], registry: {} };
    }
  }, [filesKey, effectiveFiles]);

  const gameConfig = useMemo(
    () => readGameConfigFromRegistry(compiledBundle.registry),
    [compiledBundle],
  );

  const playtestScale = useMemo(
    () => resolvePlaytestScale(gameConfig),
    [gameConfig],
  );

  const handleForceRefresh = () => {
    // Clears GitHub scene-JS localStorage only. Gallery → Storage remap is
    // live from Firestore (galleryAssets); remount still re-reads the current
    // playtestAssetUrlMap into the Phaser registry via filesKey/refreshToken.
    clearAllPlaytestArenaLocalCaches();
    setRefreshToken((n) => n + 1);
  };

  useEffect(() => {
    const parent = containerRef.current;
    if (!parent) return undefined;

    if (gameRef.current) {
      try {
        gameRef.current.destroy(true);
      } catch (destroyErr) {
        console.error('[PhaserGameMount] destroy failed:', destroyErr);
      }
      gameRef.current = null;
    }

    // Never boot while a GitHub pull is in flight — prevents stale local
    // platformer scenes from flashing into a Pong project.
    if (hasGithubRepo && githubLoading) {
      return undefined;
    }

    if (compiledBundle.errors?.length) {
      console.warn(
        '[PhaserGameMount] One or more approved files failed to compile.',
        compiledBundle.errors,
      );
    }

    const parsedScenes = Array.isArray(compiledBundle.scenes)
      ? compiledBundle.scenes
      : [];
    const sceneList = parsedScenes.length > 0 ? parsedScenes : [PlaceholderScene];
    const gravityY = readArcadeGravityY(gameConfig, 0);

    const scaleConfig = {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: playtestScale.width || PLAYTEST_DEFAULT_WIDTH,
      height: playtestScale.height || PLAYTEST_DEFAULT_HEIGHT,
    };

    try {
      const assetUrlMapForBoot = playtestAssetUrlMap;
      const game = new Phaser.Game({
        type: Phaser.AUTO,
        parent,
        backgroundColor: '#1a1a2e',
        scene: sceneList,
        physics: {
          default: 'arcade',
          arcade: {
            gravity: { y: gravityY },
            debug: false,
          },
        },
        scale: scaleConfig,
        audio: {
          noAudio: true,
        },
        banner: false,
        callbacks: {
          // Registry must be ready before scene preload / plugin onSceneStart.
          preBoot: (bootGame) => {
            bootGame.registry.set('playtestAssetUrlMap', assetUrlMapForBoot);
          },
        },
        plugins: {
          scene: [
            {
              // URL rewrite before fallback so remapped Storage loads win.
              key: 'PlaytestAssetUrlRewrite',
              plugin: PlaytestAssetUrlRewritePlugin,
              mapping: 'playtestAssetUrls',
            },
            {
              key: 'PlaytestAssetFallback',
              plugin: PlaytestAssetFallbackPlugin,
              mapping: 'playtestAssets',
            },
          ],
        },
      });
      gameRef.current = game;
    } catch (bootErr) {
      console.error('[PhaserGameMount] Phaser boot failed:', bootErr);
      try {
        const fallback = new Phaser.Game({
          type: Phaser.AUTO,
          parent,
          backgroundColor: '#1a1a2e',
          scene: [PlaceholderScene],
          scale: {
            mode: Phaser.Scale.FIT,
            autoCenter: Phaser.Scale.CENTER_BOTH,
            width: PLAYTEST_DEFAULT_WIDTH,
            height: PLAYTEST_DEFAULT_HEIGHT,
          },
          audio: { noAudio: true },
          banner: false,
        });
        gameRef.current = fallback;
      } catch (fallbackErr) {
        console.error('[PhaserGameMount] fallback boot failed:', fallbackErr);
      }
    }

    return () => {
      if (gameRef.current) {
        try {
          gameRef.current.destroy(true);
        } catch (cleanupErr) {
          console.error('[PhaserGameMount] cleanup destroy failed:', cleanupErr);
        }
        gameRef.current = null;
      }
    };
  }, [
    filesKey,
    projectKey,
    effectiveFiles,
    hasGithubRepo,
    githubLoading,
    githubFiles.length,
    playtestAssetUrlMap,
    playtestAssetUrlMapKey,
    compiledBundle,
    gameConfig,
    playtestScale,
  ]);

  const mountMaxHeight = PLAYTEST_MOUNT_MAX_HEIGHT;
  const logicalWidth = playtestScale.width || PLAYTEST_DEFAULT_WIDTH;
  const logicalHeight = playtestScale.height || PLAYTEST_DEFAULT_HEIGHT;
  // `height` prop kept for API compatibility; panel size is aspect-ratio driven.
  void height;

  return (
    <Stack spacing={1} className={className} sx={{ width: '100%' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
          {hasGithubRepo
            ? githubMeta?.commitSha
              ? `GitHub ${githubMeta.owner}/${githubMeta.repo}@${String(
                  githubMeta.commitSha,
                ).slice(0, 7)} · ${githubFiles.length} file${
                  githubFiles.length === 1 ? '' : 's'
                }${
                  remappedTextureCount
                    ? ` · ${remappedTextureCount} gallery texture${
                        remappedTextureCount === 1 ? '' : 's'
                      }`
                    : ''
                } · ${logicalWidth}×${logicalHeight} FIT`
              : githubLoading
                ? 'Pulling fresh engine files from main…'
                : 'Linked GitHub repo — use Force Refresh to pull main.'
            : effectiveFiles.length > 0
              ? `Sandbox fallback · ${effectiveFiles.length} file${
                  effectiveFiles.length === 1 ? '' : 's'
                } · ${logicalWidth}×${logicalHeight} FIT`
              : 'No engine files yet — approve patches or scaffold a GitHub repo.'}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={!hasGithubRepo || githubLoading || !companyId || !projectId}
          startIcon={
            githubLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <RefreshIcon fontSize="small" />
            )
          }
          onClick={handleForceRefresh}
          sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
        >
          Force Refresh Arena
        </Button>
      </Stack>

      {githubError ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          GitHub pull failed: {githubError} Fix the linked repo name/visibility
          or workspace PAT, then Force Refresh Arena. Sandbox history is not
          used as a fallback when a repo is linked (avoids mixing games).
        </Alert>
      ) : null}

      {platformerAssetWarning ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          {platformerAssetWarning}
        </Alert>
      ) : null}

      <Box
        sx={{
          width: '100%',
          maxHeight: mountMaxHeight,
          aspectRatio: `${logicalWidth} / ${logicalHeight}`,
          height: 'auto',
          minHeight: 280,
          borderRadius: 2,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          bgcolor: '#1a1a2e',
          position: 'relative',
          mx: 'auto',
          '& canvas': {
            display: 'block',
          },
        }}
      >
        {hasGithubRepo && githubLoading && githubFiles.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ position: 'absolute', inset: 0, zIndex: 1 }}
          >
            <CircularProgress size={28} />
            <Typography variant="caption" color="grey.400">
              Cache-busting pull from main…
            </Typography>
          </Stack>
        ) : null}
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </Box>
    </Stack>
  );
}
